import { IconButton, Stack, TableRow } from '@mui/material';
import React from 'react';

import { SelectedShipmentPackage } from './ShipmentPackagesList.props';
import {
  actionColumnSx,
  AddButton,
  ClearButton,
  dimensionColumnSx,
  NameLabel,
  quantityColumnSx,
  RemoveButton,
  StyledTableCell,
  weightColumnSx,
} from './ShipmentPackagesList.styles';
import { rowIconBtnSx } from '../../../configs/detailPanelStyles';
import {
  FreightPackageCriteria,
  FreightPackageTypeName,
  IconButtonSize,
} from '../../../configs/enums';
import { DropdownItem, SelectMenu } from '../../atoms/SelectMenu';
import { TextField, TextFieldTypeProps } from '../../atoms/TextField';

export interface ShipmentPackageRowProps {
  row: SelectedShipmentPackage;
  customPackageTypes: DropdownItem[];
  onValueChange: (packageFeature: string, id: number, value: number) => void;
  onPackageTypeChange: (id: number, value: string) => void;
  onQuantityStep: (id: number, direction: 1 | -1) => void;
  onRemove: (id: number) => void;
}

/**
 * A dimension, quantity or weight only reaches the freight order as a whole
 * positive number. Clearing the field yields `parseInt('') === NaN`, and a NaN
 * used to be written straight into the order payload.
 */
const positiveInteger = (raw: string): number | undefined => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

/** One package on the freight order. Dimensions are only editable on a custom package. */
export const ShipmentPackageRow: React.FC<ShipmentPackageRowProps> = ({
  row,
  customPackageTypes,
  onValueChange,
  onPackageTypeChange,
  onQuantityStep,
  onRemove,
}) => {
  const isCustom = row.name === FreightPackageTypeName.Custom;

  const dimension = (criteria: string, value: number, label: string) => (
    <StyledTableCell sx={dimensionColumnSx}>
      <TextField
        disabled={!isCustom}
        fullWidth
        type={TextFieldTypeProps.Number}
        inputProps={{ min: 1, 'aria-label': `${row.name} ${label}` }}
        value={value}
        onChange={(next) => {
          const value = positiveInteger(next);
          if (value !== undefined) onValueChange(criteria, row.id, value);
        }}
      />
    </StyledTableCell>
  );

  return (
    <TableRow>
      <StyledTableCell>
        <NameLabel>{row.name}</NameLabel>
      </StyledTableCell>
      {dimension(FreightPackageCriteria.Length, row.length, 'length')}
      {dimension(FreightPackageCriteria.Width, row.width, 'width')}
      {dimension(FreightPackageCriteria.Height, row.height, 'height')}
      <StyledTableCell sx={quantityColumnSx}>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <IconButton
            aria-label={`Decrease ${row.name} quantity`}
            onClick={() => onQuantityStep(row.id, -1)}
            size={IconButtonSize.Small}
          >
            <RemoveButton />
          </IconButton>
          <TextField
            fullWidth
            type={TextFieldTypeProps.Number}
            inputProps={{ min: 1, 'aria-label': `${row.name} quantity` }}
            value={row.quantity}
            onChange={(raw) => {
              const value = positiveInteger(raw);
              if (value !== undefined)
                onValueChange(FreightPackageCriteria.Quantity, row.id, value);
            }}
          />
          <IconButton
            aria-label={`Increase ${row.name} quantity`}
            onClick={() => onQuantityStep(row.id, 1)}
            size={IconButtonSize.Small}
          >
            <AddButton />
          </IconButton>
        </Stack>
      </StyledTableCell>
      <StyledTableCell sx={weightColumnSx}>
        <TextField
          fullWidth
          type={TextFieldTypeProps.Number}
          inputProps={{ min: 1, 'aria-label': `${row.name} weight` }}
          value={row.weight}
          onChange={(raw) => {
            const value = positiveInteger(raw);
            if (value !== undefined) onValueChange(FreightPackageCriteria.Weight, row.id, value);
          }}
        />
      </StyledTableCell>
      <StyledTableCell>
        {/* 100%, not the 120% it used to be — overflowing the cell is what put
            this picker under the delete button. */}
        <SelectMenu
          id={`package-type-select-${row.id}`}
          label=""
          disabled={!isCustom}
          items={customPackageTypes}
          selectedValue={row.packageType}
          minWidth="100%"
          onChange={(value) => onPackageTypeChange(row.id, value)}
        />
      </StyledTableCell>
      <StyledTableCell sx={actionColumnSx}>
        {/* The handler belongs on the button, not the icon — on the icon it was
            unreachable by keyboard. */}
        <IconButton
          size="small"
          aria-label={`Remove ${row.name} package`}
          sx={rowIconBtnSx}
          onClick={() => onRemove(row.id)}
        >
          <ClearButton />
        </IconButton>
      </StyledTableCell>
    </TableRow>
  );
};
